import {
  App,
  Button,
  Divider,
  Dropdown,
  Empty,
  FloatButton,
  Form,
  Input,
  message,
  Modal,
  Popover,
  Space,
  Tabs,
  Tooltip,
  Typography,
} from "antd";
import { ApiService } from "api/api.service";
import { IconBank, IconDocumentAdd, IconDollar, IconPlan, IconUser } from "assets/icons/onboarding";
import { AppCard } from "components/AppCard";
import { ColorBox } from "components/ColorBox";
import { GreenButtonWrapper } from "components/GreenButtonWrapper";
import PageTitle from "components/PageTitle";
import { loader, loaderSm, SingleQueryWrapper } from "components/QueryWrapper";
import DashboardLayout from "layout/DashboardLayout";
import OnboardingLayout from "layout/OnboardingLayout";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import { LuPhone, LuRocket } from "react-icons/lu";
import {
  TbBuildingBank,
  TbCheck,
  TbChevronLeft,
  TbChevronRight,
  TbCircleCheck,
  TbCircleCheckFilled,
  TbCircleDottedLetterM,
  TbClipboardCheck,
  TbCopy,
  TbDots,
  TbDotsVertical,
  TbDropCircle,
  TbFileFilled,
  TbHelp,
  TbLinkPlus,
  TbLoader2,
  TbMail,
  TbPlayerPause,
  TbPlayerPlayFilled,
  TbSend,
  TbTool,
  TbTrash,
  TbUpload,
  TbX,
} from "react-icons/tb";
import { usePlaidLink } from "react-plaid-link";
import { SetURLSearchParams, useNavigate, useSearchParams } from "react-router-dom";
import { ApplicationDto, ApplicationSchemaDto, ApplicationType } from "types/application.types";
import { UserSchemaDto } from "types/user.types";
import { HOTPROSPECTOR_LINK } from "utils/constants";
import { formatDate } from "utils/functions";
import { queryClient } from "utils/queryClient";
import { ONBOARDING_ROUTES } from "utils/routes";
import { storedTokens } from "utils/storedTokens";

type T_CardColor = "purple" | "emerald" | "cyan" | "indigo";
type T_CardStatus = "done" | "current" | "pending";
enum BankFetchMethod {
  MONEYTHUMB = "MoneyThumb",
  PLAID = "Plaid",
}

const onboardingSteps = (): {
  color: T_CardColor;
  icon: ReactNode;
  title: ReactNode;
  statusKey: keyof ApplicationDto["status"];
  route: ONBOARDING_ROUTES;
  actionIcon: ReactElement;
  actionText: string;
}[] => {
  return [
    {
      color: "cyan",
      statusKey: "loans",
      icon: <IconDocumentAdd />,
      title: "Add MCA/ Loan Documents",
      route: ONBOARDING_ROUTES.UploadLoanDocs_1,
      actionIcon: <TbUpload />,
      actionText: "Upload",
    },
    {
      color: "emerald",
      statusKey: "banks",
      icon: <IconBank />,
      title: "Upload Bank Statements",
      route: ONBOARDING_ROUTES.UploadBankDocs_1,
      actionIcon: <TbUpload />,
      actionText: "Upload",
    },
    {
      color: "purple",
      statusKey: "strategy",
      icon: <IconPlan />,
      title: "Choose Debt Relief Plan",
      route: ONBOARDING_ROUTES.ChoosePlan,
      actionIcon: <LuRocket />,
      actionText: "Select",
    },
    {
      color: "emerald",
      statusKey: "personal_info",
      icon: <IconUser />,
      title: "Provide Personal Information",
      route: ONBOARDING_ROUTES.PersonalInfo,
      actionIcon: <TbCircleCheck />,
      actionText: "Proceed",
    },
    {
      color: "indigo",
      statusKey: "signatures",
      icon: <IconDollar />,
      title: "Sign Agreements",
      route: ONBOARDING_ROUTES.SignContract,
      actionIcon: <TbPlayerPause />,
      actionText: "Pending",
    },
  ];
};

function storeOAuthTokens(searchParams: URLSearchParams, setUrlSearchParams: SetURLSearchParams) {
  const accessToken = searchParams.get("accessToken");
  const refreshToken = searchParams.get("refreshToken");

  if (accessToken && refreshToken) {
    storedTokens.accessToken.setToken(accessToken);
    storedTokens.refreshToken.setToken(refreshToken);
  }
  setUrlSearchParams("");
}

export default function OnboardingStatus() {
  const [searchParams, setUrlSearchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState<string>();
  const [chosenApplicationId, setChosenApplicationId] = useState<string>();
  const [toDeletedAppId, setToDeleteAppId] = useState<string>();
  const [allApplicationData, setAllApplicationData] = useState<ApplicationSchemaDto[]>([]);
  const [filteredApplication, setFilteredApplication] = useState<ApplicationSchemaDto[]>([]);
  const [applicationType, setApplicationType] = useState<BankFetchMethod>(BankFetchMethod.PLAID);
  const [tabKey, setTabKey] = useState<string>("all");
  const [shareModal, setShareModal] = useState({
    open: false,
    appkey: null as string | null,
  });

  const allApplication = ApiService.onboarding.useGetAllApplication();
  const createApplication = ApiService.onboarding.useCreateApplication();
  const deleteApplication = ApiService.onboarding.useDeleteApplication();
  // const validateReferrerCode = ApiService.user.useValidateReferrerCode();
  // const userProfile = ApiService.user.useGetProfile();
  useEffect(() => {
    storeOAuthTokens(searchParams, setUrlSearchParams);
  }, [searchParams]);
  useEffect(() => {
    if (allApplication.data?.result) {
      setAllApplicationData(allApplication.data.result);
      setFilteredApplication(allApplication.data.result);
    }
  }, [allApplication.data?.result]);

  const navigate = useNavigate();

  const _postCreateApplication = (affiliateCode: string | null | undefined, applicationType: BankFetchMethod) => {
    createApplication.mutate(
      { referrer_code: affiliateCode?.toUpperCase(), applicationType },
      {
        onSuccess: (data) => {
          storedTokens.selectedApplicationId.setToken(data.result._id);
          navigate(ONBOARDING_ROUTES.UploadLoanDocs_1);
        },
      },
    );
  };

  // const handleCreateApplication = (applicationType: BankFetchMethod) => {
  //   const affiliateCode = storedTokens.affiliateCode.getToken();
  //   if (referralCode) {
  //     validateReferrerCode.mutate(
  //       { referrer_code: referralCode?.toUpperCase() },
  //       {
  //         onSuccess: (data) => {
  //           if (data.result.isFound) {
  //             _postCreateApplication(referralCode, applicationType);
  //           } else if (affiliateCode) {
  //             validateReferrerCode.mutate(
  //               {
  //                 referrer_code: affiliateCode?.toUpperCase(),
  //               },
  //               {
  //                 onSuccess: (data) => {
  //                   if (data.result.isFound) {
  //                     _postCreateApplication(affiliateCode, applicationType);
  //                   } else {
  //                     // the stored affiliate code is not valid so delete it
  //                     storedTokens.affiliateCode.removeToken();
  //                     _postCreateApplication(undefined, applicationType);
  //                   }

  //                   setApplicationType(BankFetchMethod.PLAID);
  //                 },
  //               },
  //             );
  //           } else {
  //             _postCreateApplication(undefined, applicationType);
  //           }
  //         },
  //       },
  //     );
  //   } else if (affiliateCode) {
  //     validateReferrerCode.mutate(
  //       {
  //         referrer_code: affiliateCode?.toUpperCase(),
  //       },
  //       {
  //         onSuccess: (data) => {
  //           if (data.result.isFound) {
  //             _postCreateApplication(affiliateCode, applicationType);
  //           } else {
  //             // the stored affiliate code is not valid so delete it
  //             storedTokens.affiliateCode.removeToken();
  //             _postCreateApplication(undefined, applicationType);
  //           }
  //         },
  //       },
  //     );
  //   } else if (userProfile?.data?.result?.affiliate_code) {
  //     _postCreateApplication(userProfile?.data?.result?.affiliate_code, applicationType);
  //   } else {
  //     _postCreateApplication(undefined, applicationType);
  //   }
  // };

  // const handleValidateReferrerCode = () => {
  //   if (referralCode)
  //     validateReferrerCode.mutate(
  //       { referrer_code: referralCode?.toUpperCase() },
  //       {
  //         onSuccess: (data) => {
  //           if (data.result.isFound) {
  //             message.success("Referral code is valid");
  //           } else {
  //             message.error("Referral code is not valid");
  //           }
  //         },
  //       },
  //     );
  //   else return message.info("Enter code to apply");
  // };

  // const createPlaidAccessToken = ApiService.user.useCreatePlaidAccessToken();
  // const response = ApiService.user.useGetPlaidLinkToken();
  // const linkToken = response.data ?? "";

  // const onSuccess = (public_token: string, metadata: any) => {
  //   createPlaidAccessToken.mutate(
  //     { public_token },
  //     {
  //       onSuccess: () => {
  //         message.success("User has been succesfully connected to Plaid!");
  //         handleCreateApplication(BankFetchMethod.PLAID);
  //       },
  //     },
  //   );
  // };

  // const config = {
  //   token: linkToken,
  //   onSuccess,
  // };

  // const { open, ready } = usePlaidLink(config);

  const handleApplicationSearch = (searchText: string, tabKey: string) => {
    let data = allApplicationData;
    if (tabKey !== "all") {
      data = data.filter((app) => app.application_type === tabKey);
    }
    if (searchText) {
      data = data.filter((app) => {
        const businessName = app.business_info?.company_name ?? "";
        const appKey = app.app_key ?? "";
        return (
          businessName.toLowerCase().includes(searchText.toLowerCase()) ||
          appKey.toLowerCase().includes(searchText.toLowerCase())
        );
      });
    }
    setFilteredApplication(data);
  };

  const renderStatus = (allApplicationData: ApplicationSchemaDto[]) => {
    const loginAsOther = ApiService.user.useLoginAsOther();
    if (allApplicationData?.length === 0) {
      return (
        <>
          <Space.Compact>
            <Input
              size="large"
              placeholder="Enter affiliate code..."
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
            />
            <Button size="large">Apply</Button>
          </Space.Compact>
          <Button
            size="large"
            type="primary"
            icon={<TbChevronRight />}
            iconPosition="end"
            className="w-fit mx-auto mt-8"
            // onClick={() => handleCreateApplication(BankFetchMethod.MONEYTHUMB)}
            // loading={createApplication.isPending || validateReferrerCode.isPending}
            block
          >
            Start Here
          </Button>
        </>
      );
    } else if (allApplicationData?.length === 1 || chosenApplicationId) {
      const selected = chosenApplicationId
        ? allApplicationData?.find((el) => el._id === chosenApplicationId)
        : allApplicationData?.[0];

      // const isNoneDone = onboardingSteps().every((el) => selected?.status[el.statusKey] === false);
      const lastStepIndex =
        selected?.status?.last_step?.length === 0
          ? 0
          : onboardingSteps()?.findIndex((el) => el?.statusKey === selected?.status?.last_step) + 1;
      const lastStep =
        onboardingSteps()?.find((el) => el?.statusKey === selected?.status?.last_step) ?? onboardingSteps()?.[0];
      return (
        <>
          <Space.Compact>
            <Input
              size="large"
              placeholder="Enter affiliate code..."
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
            />
            <Button size="large">Apply</Button>
          </Space.Compact>
          <div className="flex flex-col gap-3">
            <div className="flex gap-4 justify-between mb-4 mt-2">
              {/* {userProfile.data?.result.plaid_config?.connected ? (
                <Button
                  size="large"
                  type="primary"
                  icon={<TbChevronRight />}
                  iconPosition="end"
                  className="w-fit mx-auto"
                  onClick={() => {
                    handleCreateApplication(BankFetchMethod.PLAID);
                  }}
                  loading={createApplication.isPending || validateReferrerCode.isPending}
                  block
                >
                  Start with existing Plaid account
                </Button>
              ) : null} */}
              <Button
                size="large"
                type="primary"
                icon={<TbChevronRight />}
                iconPosition="end"
                className="w-fit mx-auto"
                onClick={() => {
                  setApplicationType(BankFetchMethod.PLAID);
                  open();
                }}
                // disabled={!ready}
                // loading={createApplication.isPending || validateReferrerCode.isPending}
                block
              >
                Start With new Plaid account
              </Button>
            </div>

            <div className="flex items-center">
              <div className="flex-grow border border-solid border-t border-gray-300"></div>
              <span className="mx-4 text-gray-500">OR</span>
              <div className="flex-grow border border-solid border-t border-gray-300"></div>
            </div>

            <Button
              size="large"
              variant="filled"
              // color="primary"
              icon={<TbChevronRight />}
              iconPosition="end"
              className="w-fit mx-auto mt-3 mb-4"
              // onClick={() => {
              //   handleCreateApplication(BankFetchMethod.MONEYTHUMB);
              // }}
              // loading={createApplication.isPending || validateReferrerCode.isPending}
              block
            >
              Start New Application Manually
            </Button>

            <Button
              size="large"
              type="primary"
              icon={<TbChevronRight />}
              iconPosition="end"
              className="w-fit mx-auto mt-3 mb-8"
              onClick={() => {
                navigate(lastStep?.route);
              }}
              block
            >
              Continue Where You Left
            </Button>
          </div>
          {onboardingSteps().map((step, i) => {
            const status: T_CardStatus = i == lastStepIndex ? "current" : i < lastStepIndex ? "done" : "pending";

            const renderAction = () => {
              const btn = (
                <Button
                  disabled={status === "pending"}
                  icon={step.actionIcon}
                  type="primary"
                  onClick={() => {
                    storedTokens.selectedApplicationId.setToken(selected?._id as any);
                    navigate(step.route);
                  }}
                >
                  {status === "done" ? "Done" : step.actionText}
                </Button>
              );
              if (status === "done") {
                return <GreenButtonWrapper>{btn}</GreenButtonWrapper>;
              } else return btn;
            };

            return (
              <AppCard.OnboardingSteps status={status} key={i}>
                <ColorBox color={step.color}>{step.icon}</ColorBox>
                <Typography.Text className={`mr-auto ${status === "current" ? "!font-bold" : ""}`}>
                  {step.title}
                </Typography.Text>
                {renderAction()}
              </AppCard.OnboardingSteps>
            );
          })}
          {allApplicationData.length > 1 ? (
            <Button
              variant="filled"
              color="primary"
              icon={<TbChevronLeft />}
              className="w-fit mx-auto mt-8 border-purple-300"
              onClick={() => setChosenApplicationId(undefined)}
            >
              Go to All Applications
            </Button>
          ) : null}
        </>
      );
    } else {
      return (
        <>
          <Space.Compact>
            <Input
              size="large"
              placeholder="Enter affiliate code..."
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
            />
            <Button
              size="large"
              //  loading={validateReferrerCode.isPending}
              //   onClick={handleValidateReferrerCode}
            >
              Apply
            </Button>
          </Space.Compact>

          <div className="flex gap-4 justify-between mb-4 mt-2">
            {/* {userProfile.data?.result.plaid_config?.connected ? (
              <Button
                size="large"
                type="primary"
                icon={<TbChevronRight />}
                iconPosition="end"
                className="w-fit mx-auto"
                onClick={() => handleCreateApplication(BankFetchMethod.PLAID)}
                loading={createApplication.isPending || validateReferrerCode.isPending}
                block
              >
                Start with existing Plaid
              </Button>
            ) : null} */}
            <Button
              size="large"
              type="primary"
              icon={<TbChevronRight />}
              iconPosition="end"
              className="w-fit mx-auto"
              onClick={() => open()}
              // disabled={!ready}
              // loading={createApplication.isPending || validateReferrerCode.isPending}
              block
            >
              Start With new Plaid
            </Button>
          </div>

          <div className="flex items-center">
            <div className="flex-grow border border-solid border-t border-gray-300"></div>
            <span className="mx-4 text-gray-500">OR</span>
            <div className="flex-grow border border-solid border-t border-gray-300"></div>
          </div>

          <Button
            size="large"
            type="primary"
            icon={<TbChevronRight />}
            iconPosition="end"
            className="w-fit mx-auto mt-3 mb-4"
            // onClick={() => handleCreateApplication(BankFetchMethod.MONEYTHUMB)}
            // loading={createApplication.isPending || validateReferrerCode.isPending}
            block
          >
            Start New Application Manually
          </Button>
          {/* {userProfile?.data?.result?.affiliate_code && (
            <div>
              <Input.Search
                onSearch={(value) => {
                  handleApplicationSearch(value, tabKey);
                }}
                onChange={(e) => {
                  handleApplicationSearch(e.target.value, tabKey);
                }}
                style={{ width: "100%", marginBottom: "4px" }}
                placeholder="Search application by name or app key"
              />
              <Tabs
                centered
                style={{ width: "100%" }}
                defaultActiveKey="all"
                items={[
                  {
                    key: "all",
                    label: "All Applications",
                    // children: "Content of Tab Pane 1",
                  },
                  {
                    key: ApplicationType.AFFILIATE,
                    label: "Affiliate Applications",
                    // children: "Content of Tab Pane 2",
                  },
                  {
                    key: ApplicationType.DROP,
                    label: "Drop Applications",
                    // children: "Content of Tab Pane 3",
                  },
                ]}
                onChange={(key) => {
                  setTabKey(key);
                  if (key === "all") {
                    setFilteredApplication(allApplicationData);
                  } else {
                    setFilteredApplication(
                      allApplicationData.filter((el) => el.application_type === (key as ApplicationType)),
                    );
                  }
                }}
              />
            </div>
          )} */}
          {filteredApplication.length === 0 ? (
            <div className="flex items-center justify-center">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No application found" />
            </div>
          ) : (
            filteredApplication.map((application) => {
              const businessName =
                application?.business_info?.company_name && application?.business_info?.company_name?.length > 0
                  ? application?.business_info?.company_name
                  : undefined;

              const email =
                application?.business_info?.email && application?.business_info?.email?.length > 0
                  ? application?.business_info?.email
                  : "";

              return (
                <div
                  key={application._id}
                  className={`border border-solid bg-white border-gray-300 rounded-xl p-3 flex items-center justify-between`}
                >
                  {businessName ? (
                    <div className="flex flex-col gap-2">
                      <div className="inline">
                        <Typography.Text className={`font-bold !tabular-nums !m-0`}>{businessName}</Typography.Text>
                        {application?.managers_approval ? (
                          <Tooltip
                            title={`Manager's approval: ${application?.managers_approval?.status?.toUpperCase()}`}
                          >
                            <Button
                              icon={<TbCircleDottedLetterM className="stroke-red-500" />}
                              shape="circle"
                              size="small"
                              className="ml-2"
                            />
                          </Tooltip>
                        ) : null}
                      </div>
                      <div className="inline-flex gap-3">
                        <Typography.Text type="secondary" className={`font-bold text-sm !tabular-nums !text-gray-400`}>
                          {application.app_key?.toUpperCase()}
                        </Typography.Text>
                        <Typography.Text type="secondary" className="mr-auto ml-2 text-sm">
                          Updated {formatDate(application.updatedAt)}
                        </Typography.Text>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 items-center">
                      <Typography.Text className={`font-bold !tabular-nums`}>
                        {application.app_key?.toUpperCase()}
                      </Typography.Text>
                      <Typography.Text type="secondary" className="mr-auto ml-2 text-sm">
                        Updated {formatDate(application.updatedAt)}
                      </Typography.Text>
                    </div>
                  )}
                  <div className="inline-flex gap-3 flex-shrink-0 items-center">
                    {storedTokens.selectedApplicationId.getToken() === application?._id ? (
                      <Button
                        className="!flex-shrink-0"
                        icon={<TbPlayerPlayFilled />}
                        type="primary"
                        onClick={() => {
                          setChosenApplicationId(application._id);
                        }}
                      >
                        Continue
                      </Button>
                    ) : (
                      <GreenButtonWrapper>
                        <Button
                          className="!flex-shrink-0"
                          icon={<TbPlayerPlayFilled />}
                          type="primary"
                          onClick={() => {
                            storedTokens.selectedApplicationId.setToken(application._id);
                            setChosenApplicationId(application._id);
                          }}
                        >
                          Resume
                        </Button>
                      </GreenButtonWrapper>
                    )}
                    <Dropdown
                      trigger={["click"]}
                      // menu={{
                      //   items: [
                      //     ...(email.length &&
                      //     userProfile.data?.result.affiliate_code &&
                      //     userProfile.data?.result.email !== email &&
                      //     application.application_type === ApplicationType.AFFILIATE
                      //       ? [
                      //           {
                      //             key: "login-as-other",
                      //             label: (
                      //               <span
                      //                 onClick={() => {
                      //                   Modal.confirm({
                      //                     title: "Are you sure you want to login as this user?",
                      //                     okText: "Yes",
                      //                     cancelText: "No",
                      //                     centered: true,
                      //                     onOk: () => {
                      //                       loginAsOther.mutate(
                      //                         { email },
                      //                         {
                      //                           onSuccess: () => {
                      //                             message.success(`Succesfully logged in!`);
                      //                           },
                      //                         },
                      //                       );
                      //                     },
                      //                   });
                      //                 }}
                      //               >
                      //                 Login as this user
                      //               </span>
                      //             ),
                      //           },
                      //         ]
                      //       : []),
                      //     // {
                      //     //   key: "share-application",
                      //     //   label: (
                      //     //     <span
                      //     //       onClick={() => {
                      //     //         setShareModal({
                      //     //           open: true,
                      //     //           appkey: application.app_key,
                      //     //         });
                      //     //       }}
                      //     //     >
                      //     //       Share application
                      //     //     </span>
                      //     //   ),
                      //     // },
                      //     {
                      //       key: "delete",
                      //       disabled: !(
                      //         application?.statement_files?.length === 0 &&
                      //         application?.banks?.length === 0 &&
                      //         application?.status?.banks === false
                      //       ),
                      //       label: (
                      //         <span
                      //           style={{
                      //             color:
                      //               application?.statement_files?.length === 0 &&
                      //               application?.banks?.length === 0 &&
                      //               application?.status?.banks === false
                      //                 ? "red"
                      //                 : "#999",
                      //           }}
                      //           onClick={() => {
                      //             if (
                      //               application?.statement_files?.length === 0 &&
                      //               application?.banks?.length === 0 &&
                      //               application?.status?.banks === false
                      //             ) {
                      //               setToDeleteAppId(application._id);
                      //             }
                      //           }}
                      //         >
                      //           Delete
                      //         </span>
                      //       ),
                      //     },
                      //   ],
                      // }}
                    >
                      <TbDotsVertical style={{ fontSize: 18, cursor: "pointer" }} />
                    </Dropdown>
                  </div>
                </div>
              );
            })
          )}
        </>
      );
    }
  };

  const shareApplication = ApiService.onboarding.useShareApplication();
  const [shareForm] = Form.useForm();

  return (
    <DashboardLayout>
      <OnboardingLayout stepsShow={false}>
        <PageTitle pageTitle={[]} title="Start Onboarding" />
        <Modal
          title="Share Application"
          open={shareModal.open}
          onCancel={() => {
            setShareModal({ open: false, appkey: null });
            shareForm.resetFields();
          }}
          onOk={() => {
            const email = shareForm.getFieldValue("email");

            shareApplication.mutate(
              {
                email,
                appkey: shareModal.appkey ?? "",
              },
              {
                onSuccess: () => {
                  shareForm.resetFields();
                  setShareModal({ open: false, appkey: null });
                  message.success(`Application shared succesfully!`);
                },
              },
            );
          }}
        >
          <Form layout="vertical" form={shareForm}>
            <Form.Item
              label="Recipient Email"
              name="email"
              rules={[
                { required: true, message: "Please enter an email" },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <Input placeholder="example@gmail.com" />
            </Form.Item>
          </Form>
        </Modal>
        <div className="flex flex-col justify-center items-center p-5">
          {/* <img role="presentation" src="/onboarding-doc.png" />

        <Typography.Title level={4} className="!text-center">
          Welcome to {orgMeta.company_name}, your one-stop Management Platform for all your Debts!
        </Typography.Title>
        <Typography.Text type="secondary" className="!text-center">
          To get quick debt resolution, we please follow the given steps in order.
          <br />
          This is important for us to optimise the best resolution for your case and free you from Debt.
        </Typography.Text> */}

          <div>
            <div className="mb-8 mt-12">
              <Typography.Title level={1} className="!text-center">
                3 simple steps ...
              </Typography.Title>
            </div>
            <div className="flex flex-col gap-3 mb-6 max-w-[560px] w-full">
              <div className="flex w-full items-center gap-2">
                <AppCard className="border-sky-300 bg-sky-50 rounded-[20px] p-2 w-full">
                  <AppCard className="border-transparent p-2 flex gap-2 items-center">
                    <div className="inline-flex p-3 rounded-full bg-sky-100 items-center justify-center">
                      <TbFileFilled className="fill-sky-500 size-6" />
                    </div>
                    <Typography.Text className="!text-sky-800 font-medium">1. Tell us about your debt</Typography.Text>
                  </AppCard>
                </AppCard>
                {/* <div className="size-10 inline-flex items-center justify-center bg-purple-200 rounded-full">
              <Typography.Text className="!font-bold !text-purple-700">1</Typography.Text>
            </div> */}
              </div>
              <div className="flex w-full items-center gap-2">
                <AppCard className="border-orange-300 bg-orange-50 rounded-[20px] p-2 w-full">
                  <AppCard className="border-transparent p-2 flex gap-2 items-center">
                    <div className="inline-flex p-3 rounded-full bg-orange-100 items-center justify-center">
                      <TbBuildingBank className="stroke-orange-500 size-6" />
                    </div>
                    <Typography.Text className="!text-orange-800 font-medium">
                      2. Tell us about your business
                    </Typography.Text>
                  </AppCard>
                </AppCard>
                {/* <div className="size-10 inline-flex items-center justify-center bg-purple-200 rounded-full">
              <Typography.Text className="!font-bold !text-purple-700">2</Typography.Text>
            </div> */}
              </div>
              <div className="flex w-full items-center gap-2">
                <AppCard className="border-green-300 bg-green-50 rounded-[20px] p-2 w-full">
                  <AppCard className="border-transparent p-2 flex gap-2 items-center">
                    <div className="inline-flex p-3 rounded-full bg-green-100 items-center justify-center">
                      <TbCircleCheckFilled className="fill-green-500 size-6" />
                    </div>
                    <Typography.Text className="!text-green-800 font-medium">
                      And your debt free journey starts now
                    </Typography.Text>
                  </AppCard>
                </AppCard>
                {/* <div className="size-10 inline-flex items-center justify-center bg-purple-200 rounded-full">
              <Typography.Text className="!font-bold !text-purple-700">3</Typography.Text>
            </div> */}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 max-w-[560px] w-full mt-6">
            <SingleQueryWrapper
              query={allApplication}
              renderData={(allApplicationData) => {
                return renderStatus(allApplicationData.result);
              }}
            />
          </div>
          <CallButton />
          {/* {userProfile?.data?.result?.affiliate_code ? <AffiliateButton /> : null} */}
        </div>

        <Modal
          open={!!toDeletedAppId}
          title="Are you sure you want to delete the application"
          okButtonProps={{ loading: deleteApplication.isPending || allApplication.isRefetching }}
          onOk={() => {
            if (toDeletedAppId)
              deleteApplication.mutate(
                { application_id: toDeletedAppId },
                {
                  onSuccess: () => {
                    message.success(`Application deleted`);
                    allApplication.refetch().then(() => {
                      setToDeleteAppId(undefined);
                    });
                  },
                },
              );
          }}
          onCancel={() => {
            setToDeleteAppId(undefined);
          }}
        />
      </OnboardingLayout>
    </DashboardLayout>
  );
}

function CallButton() {
  const [open, setOpen] = useState(false);

  const hide = () => {
    setOpen(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      content={
        <div className="w-[300px]">
          <div className="flex items-center justify-between mb-3">
            <Typography.Text className="!text-gray-800 !font-semibold">Need Help?</Typography.Text>
            <Button icon={<TbX />} type="text" onClick={hide} size="small" />
          </div>
          <Typography.Paragraph type="secondary">
            If you require assistance in setting your profile, our team is here to assist you.
            <br />
            Tap the button below to get a call back.
          </Typography.Paragraph>
          <div className="flex justify-center">
            <Button
              variant="filled"
              color="primary"
              icon={<LuPhone className="fill-purple-700" />}
              className="border-purple-500"
              href={HOTPROSPECTOR_LINK}
              target="_blank"
            >
              Get a Call Back
            </Button>
          </div>
        </div>
      }
    >
      <FloatButton icon={<TbHelp />} />
    </Popover>
  );
}

type T_LinkType = "quick-link" | "magic-link";

function AffiliateButton() {
  const [open, setOpen] = useState(false);
  const generateDropLink = ApiService.onboarding.useCreateExternalLink();
  const [linkType, setLinkType] = useState<T_LinkType>("quick-link");
  const [email, setEmail] = useState("");

  return (
    <>
      <Dropdown
        trigger={["click"]}
        placement="topLeft"
        menu={{
          items: [
            {
              label: "Generate Quick Link",
              icon: <TbLinkPlus size={20} />,
              key: "quick-link",
              // icon: generateDropLink.isPending ? loader : <TbLinkPlus />,
              onClick: () => {
                setLinkType("quick-link");
                generateDropLink.mutate(
                  { email },
                  {
                    onSuccess: (data) => {
                      setOpen(true);
                    },
                  },
                );
              },
            },
            {
              label: "Send Magic Link",
              icon: <TbMail size={20} />,
              key: "magic-link",
              // icon: generateDropLink.isPending ? loader : <TbLinkPlus />,
              onClick: () => {
                setLinkType("magic-link");
                setOpen(true);
                // generateDropLink.mutate(undefined, {
                //   onSuccess: (data) => {
                //     setOpen(true);
                //   },
                // });
              },
            },
          ],
        }}
      >
        <Tooltip placement="right" title="Affiliate Tools">
          <FloatButton style={{ left: 24 }} icon={generateDropLink.isPending ? loaderSm : <TbTool />} />
        </Tooltip>
      </Dropdown>
      <Modal
        open={open}
        centered
        onCancel={() => {
          setOpen(false);
        }}
        footer={null}
        title={linkType === "quick-link" ? "Your Quick Link" : "Send Magic Link"}
      >
        {linkType === "quick-link" && (
          <Space.Compact style={{ width: "100%" }}>
            <Input style={{ width: "100%" }} readOnly defaultValue={generateDropLink?.data?.result?.url} />
            <Typography.Text
              copyable={{
                text: generateDropLink?.data?.result?.url,
                icon: [
                  <Button key={"copy"} variant="solid" color="primary" className="w-[120px]" icon={<TbCopy />}>
                    Copy
                  </Button>,
                  <Button
                    key={"copied"}
                    variant="solid"
                    color="green"
                    className="w-[120px] !bg-emerald-500"
                    icon={<TbClipboardCheck />}
                  >
                    Copied
                  </Button>,
                ],
              }}
            />
          </Space.Compact>
        )}
        {linkType === "magic-link" && (
          <Form
            onFinish={() => {
              if (!email) return message.error("Enter email");
              //validate email
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(email)) return message.error("Enter a valid email");
              generateDropLink.mutate(
                { email },
                {
                  onSuccess: (data) => {
                    message.success(`Magic link sent to ${email}`);
                    setEmail("");
                    setOpen(false);
                  },
                },
              );
            }}
          >
            <Space.Compact style={{ width: "100%" }}>
              <Input
                onChange={(e) => setEmail(e.target.value)}
                style={{ width: "100%" }}
                value={email}
                type="email"
                placeholder="enter email"
              />
              {/* <Typography.Text
            copyable={{
              text: generateDropLink?.data?.result?.url,
              icon: [
                <Button key={"copy"} variant="solid" color="primary" className="w-[120px]" icon={<TbCopy />}>
                  Copy
                </Button>,
                <Button
                  key={"copied"}
                  variant="solid"
                  color="green"
                  className="w-[120px] !bg-emerald-500"
                  icon={<TbClipboardCheck />}
                >
                  Copied
                </Button>,
              ],
            }}
          /> */}
              <Button
                htmlType="submit"
                // onClick={}
                loading={generateDropLink.isPending}
                key={"copy"}
                variant="solid"
                color="primary"
                className="w-[120px]"
                icon={<TbSend />}
              >
                Send
              </Button>
            </Space.Compact>
          </Form>
        )}
        {/* <Divider>
          <Typography.Text type="secondary">OR</Typography.Text>
        </Divider>
        <Space.Compact style={{ width: "100%" }}>
          <Input style={{ width: "100%" }} placeholder="Customer email address" />
          <Button type="primary">Send Email</Button>
        </Space.Compact> */}
      </Modal>
    </>
  );
}
